import React, { Fragment } from 'react';
import { EventSectionSaleProductReport } from '@/model/EventSectionSaleProductReport';
import { CustomTable, TableColumn } from '@/components/Table';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventSectionProductReport } from '@/model/EventSectionProductReport';
import { EventSectionMachineReport } from '@/model/EventSectionMachineReport';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { ReportDropdownHeader } from '../../ReportDropdownHeader';

interface StateProps {
  sections: EventSectionSaleProductReport[];
}

export const SaleProductSectionsContent: React.FC<StateProps> = ({ sections }): JSX.Element => {
  const getColumns = (products: EventSectionProductReport[]): TableColumn<any>[] => {
    let showStock = false;
    let showCommission = false;

    const columns: TableColumn<any>[] = [
      {
        name: 'Produto',
        selector: row => row.productName,
      },
      {
        name: 'Vendidos',
        selector: row => row.productSold,
      },
      {
        name: 'Valor',
        selector: row => row.unitValue,
      },
      {
        name: 'Venda',
        selector: row => row.totalSaleValue,
      },
    ];

    // eslint-disable-next-line no-plusplus
    for (let p = 0; p < products.length; p++) {
      if (products[p].initialInventory > 0 || products[p].availableStock > 0) {
        showStock = true;
      }
      if (products[p].totalCommission > 0) {
        showCommission = true;
      }
      if (showStock && showCommission) {
        break;
      }
    }
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    if (showStock) {
      columns.push({
        name: 'Estoque inicial',
        selector: row => row.initialInventory,
      });
      columns.push({
        name: 'Estoque disponível',
        selector: row => row.availableStock,
      });
    }
    return columns;
  };

  const getColumnMachines = (machines: EventSectionMachineReport[]): TableColumn<any>[] => {
    let showCourtesy = false;
    let showDiscount = false;
    let showCommission = false;
    const columns: TableColumn<any>[] = [
      {
        name: 'Serial',
        selector: row => row.serialNumber,
      },
      {
        name: 'Vendidos',
        selector: row => row.amountSold,
      },
    ];
    // eslint-disable-next-line no-plusplus
    for (let m = 0; m < machines.length; m++) {
      if (machines[m].courtesies > 0) {
        showCourtesy = true;
      }
      if (machines[m].discount > 0) {
        showDiscount = true;
      }
      if (machines[m].totalCommission > 0) {
        showCommission = true;
      }
      if (showCourtesy && showDiscount && showCommission) {
        break;
      }
    }
    if (showCourtesy) {
      columns.push({
        name: 'Cortesias',
        selector: row => row.courtesies,
      });
    }
    if (showDiscount) {
      columns.push({
        name: 'Desconto',
        selector: row => row.discount,
      });
    }
    columns.push({
      name: 'Venda',
      selector: row => row.totalSaleValue,
    });
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    return columns;
  };

  return (
    <Fragment>
      {sections.map(data => (
        <div key={data.id} className="mb-3">
          <SuperCollapse
            title={
              <ReportDropdownHeader
                title={data.name}
                summary={data.summary}
                payments={data.payments}
              />
            }
            content={
              <div className="p-3 bg-white rounded">
                {data.categories.map(category => (
                  <div key={category.id} className="mb-4">
                    <h6 className="font-weight-bold mb-2 text-secondary">{category.name}</h6>
                    <CustomTable
                      theme="secondaryWithoutBorder"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={getColumns(category.products)}
                      data={
                        category.products.map(product => ({
                          productName: product.name,
                          productSold: product.amountSold,
                          initialInventory: product.initialInventory,
                          availableStock: product.availableStock,
                          unitValue: toCurrency(product.unitValue),
                          totalSaleValue: toCurrency(product.totalSaleValue),
                          totalCommission: toCurrency(product.totalCommission),
                          totalSaleWithCommission: toCurrency(product.totalSaleWithCommission),
                        })) || []
                      }
                    />
                  </div>
                ))}

                {data.machines && data.machines.length > 0 && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2 text-secondary">POS</h6>
                    <CustomTable
                      theme="secondaryWithoutBorder"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={getColumnMachines(data.machines)}
                      data={
                        data.machines.map(machine => ({
                          serialNumber: machine.serialNumber,
                          amountSold: machine.amountSold,
                          courtesies: machine.courtesies,
                          discount: toCurrency(machine.discount),
                          totalSaleValue: toCurrency(machine.totalSaleValue),
                          totalCommission: toCurrency(machine.totalCommission),
                          totalSaleWithCommission: toCurrency(machine.totalSaleWithCommission),
                        })) || []
                      }
                    />
                  </div>
                )}
              </div>
            }
          />
        </div>
      ))}
    </Fragment>
  );
};
